/**
 * /field/wt/jobs/:id/log/:workDate/:shift — the hand's shift log for one day
 * and shift, saved with PUT /well-testing/field/jobs/:id/shift-logs/:workDate/:shift
 * (fieldiq-api#1245). SNAKE_CASE reads.
 *
 * The PUT is an upsert: the first save creates the log (and moves a scheduled
 * job to rigging up — the API's rule, not this screen's), a later save
 * replaces it. A future day is refused by the API (400 future_day) and the
 * refusal is shown as a sentence. Hour-meter warnings come back in `warnings`
 * and are shown after the save; they do not block it.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSaveWtFieldLog, useWtFieldJob, useWtFieldLog } from '../../../hooks/useWellTesting';
import { wtDay, wtErrorCode, wtJobLoggable, wtRefusal } from '../../../lib/well-testing';
import type { WtShiftLogWarning } from '../../../types/well-testing';
import { PtFieldPage, PtFieldQueryState, PtRefusal } from '../../pressure-testing/field/PtFieldBits';
import { fieldLogOptions, fieldLogValues } from '../logOptions';
import { ShiftLogEditor } from '../ShiftLogEditor';
import { LogWarnings } from '../ShiftLogScreen';
import { wtNotOnCrewMessage } from './WtFieldJobScreen';

export default function WtFieldShiftLogScreen() {
  const { id, workDate, shift } = useParams<{ id: string; workDate: string; shift: string }>();
  const jobQuery = useWtFieldJob(id);
  const logQuery = useWtFieldLog(id, workDate, shift);
  const save = useSaveWtFieldLog();
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<WtShiftLogWarning[]>([]);
  const [saved, setSaved] = useState(0);
  const job = jobQuery.data?.data ?? null;
  const refused = wtNotOnCrewMessage(jobQuery);
  // A 404 on the log is "not written yet", not a failure.
  const logMissing = wtErrorCode(logQuery.error) === 'not_found' || (logQuery.error as { response?: { status?: number } })?.response?.status === 404;
  const log = logQuery.data?.data ?? null;
  const shiftLabel = shift === 'night' ? 'Night' : 'Day';
  const options = job ? fieldLogOptions(job) : { crew: [], units: [] };
  const unitName = (equipmentId?: string) => options.units.find((u) => u.equipmentId === equipmentId)?.name ?? null;

  return (
    <PtFieldPage title={`${shiftLabel} log · ${wtDay(workDate)}`} backTo={id ? `/field/wt/jobs/${id}` : '/field/wt'} testId="wt-field-shift-log">
      {refused ? (
        <PtRefusal message={refused} testId="wt-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={jobQuery} subject="this job">
          {!job ? (
            <PtRefusal message="That job no longer exists. It may have been deleted." />
          ) : !wtJobLoggable(job.status) ? (
            <PtRefusal message="This job is not open for shift logs." />
          ) : logQuery.isLoading ? (
            <p className="text-sm text-fieldiq-muted">Loading the log…</p>
          ) : logQuery.error && !logMissing ? (
            <PtFieldQueryState query={logQuery} subject="this shift log">
              {null}
            </PtFieldQueryState>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-fieldiq-text">
                {job.job_number} · {job.well_name || '—'}
              </p>
              {warnings.length > 0 && <LogWarnings warnings={warnings} unitName={unitName} />}
              <ShiftLogEditor
                key={`${log?.id ?? 'new'}-${saved}`}
                workDate={workDate ?? ''}
                shift={shift ?? 'day'}
                values={fieldLogValues(log)}
                crewOptions={options.crew}
                unitOptions={options.units}
                pending={save.isPending}
                error={error}
                submitLabel={log ? 'Save changes' : 'Save shift log'}
                onSubmit={(body) => {
                  setError(null);
                  if (!id || !workDate || !shift) return;
                  save.mutate(
                    { jobId: id, day: workDate, shift, body },
                    {
                      onSuccess: (res) => {
                        toast.success('Shift log saved');
                        setWarnings(res.warnings ?? []);
                        setSaved((n) => n + 1);
                      },
                      onError: (err) => setError(wtRefusal(err, 'The shift log could not be saved.')),
                    },
                  );
                }}
              />
            </div>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
